import { faBan, faCalendarDays, faCheck, faCreditCard, faCreditCardFront, faFilePen, faRotateRight, faTrash, faUserSlash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { ConfirmDialog, useConfirmDialogComponent } from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { fetchVisitAvailablePackages } from 'src/models/visit/visit';
import { hasFeatureFlag, usePermissionCheck } from 'src/shared/usePermissionCheck';
import { formatCurrency } from 'src/utils/numberFormatter';

import { VisitPackageInfo } from './VisitPackageInfo';
import { VisitUserInfo } from './VisitUserInfo';

import type { Appointment } from 'src/models/appointment/_types';
import type { Visit } from 'src/models/visit/_types';

interface VisitRowProps {
  visit: Visit;
  appointment: Appointment;
  canManage: boolean;
  isRecurring?: boolean;
  isLoading?: boolean;
  onMarkCompleted?: (visit: Visit) => void;
  onMarkNoShow?: (visit: Visit, withFee?: boolean) => void;
  onMarkCancelled?: (visit: Visit) => void;
  onMarkLateCancelled?: (visit: Visit, withFee?: boolean) => void;
  onResetStatus?: (visit: Visit) => void;
  onDontUsePackage?: (visit: Visit) => void;
  onAttemptPackageDeduction?: (visit: Visit) => void;
  onChargeDefaultPackage?: (visit: Visit) => void;
  onDeleteVisit?: (visit: Visit) => void;
  // New actions
  onAddCard?: (visit: Visit) => void;
  onManageNotes?: (visit: Visit) => void;
  onManageRecurringMembership?: (visit: Visit) => void;
  onAssignPackage?: (visit: Visit) => void;
  onChargeProduct?: (visit: Visit) => void;
}

// Status chip colors (labels are translation keys)
const STATUS_CONFIG: Record<
  string,
  { labelKey: string; color: 'success' | 'error' | 'warning' | 'primary' | 'secondary' }
> = {
  completed: { labelKey: 'checkedIn', color: 'success' },
  registered: { labelKey: 'going', color: 'primary' },
  reserved: { labelKey: 'reserved', color: 'secondary' },
  cancelled: { labelKey: 'cancelled', color: 'error' },
  late_cancelled: { labelKey: 'lateCancelled', color: 'error' },
  noshowed: { labelKey: 'noShow', color: 'warning' },
};

/**
 * VisitRow - Individual visit display with actions
 *
 * Clean layout with:
 * - Status chip for visual clarity
 * - All info visible (no collapsing)
 * - Single-click actions as inline links
 */
export const VisitRow: FC<VisitRowProps> = ({
  visit,
  appointment,
  canManage,
  isRecurring = false,
  isLoading = false,
  onMarkCompleted,
  onMarkNoShow,
  onMarkCancelled,
  onMarkLateCancelled,
  onResetStatus,
  onDontUsePackage,
  onAttemptPackageDeduction,
  onChargeDefaultPackage,
  onDeleteVisit,
  onAddCard,
  onManageNotes,
  onManageRecurringMembership,
  onAssignPackage,
  onChargeProduct,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.visitRow' });
  const { session } = useSession();
  const useNewJournal =
    !!session && hasFeatureFlag(session, FeatureFlag.NewJournal);
  const { canCancelVisit, canEarlyCancelVisit } = usePermissionCheck();
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();

  // Fetch available packages to determine if "Charge Default" should show
  const { data: availablePackages = [] } = useQuery({
    queryKey: ['visitAvailablePackages', visit.id],
    queryFn: () => fetchVisitAvailablePackages(visit.id),
    enabled: !!visit.id,
  });

  const isCancelled =
    visit.status === 'cancelled' || visit.status === 'late_cancelled';
  const isClosedOut =
    visit.status === 'completed' || visit.status === 'noshowed' || isCancelled;

  const statusConfig = STATUS_CONFIG[visit.status] || {
    labelKey: visit.status,
    color: 'secondary' as const,
  };

  const service = appointment.service;
  const hasNoshowFee = service?.noshowFee;
  const hasCancellationFee = service?.cancellationFee;
  const hasDefaultPackage = service?.defaultPackageId;
  const hasMembership = !!(visit as any).hasMembership;
  const hasNoAvailablePackages = availablePackages.length === 0;

  // Handler for charging default package with confirmation
  const handleChargeDefaultPackage = async () => {
    const confirmed = await confirm({
      title: t('chargeDefaultPackageTitle'),
      message: t('chargeDefaultPackageMessage'),
      confirmText: t('chargeDefaultPackageConfirm'),
      cancelText: t('chargeDefaultPackageCancel'),
      confirmColor: 'primary',
    });
    if (confirmed) {
      onChargeDefaultPackage?.(visit);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor:
          visit.color || (isCancelled ? 'error.main' : 'primary.main'),
        opacity: isCancelled ? 0.7 : 1,
      }}
    >
      {/* Row 1: User Info + Status Chip */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <VisitUserInfo
            userName={visit.userName || ''}
            userId={visit.userId}
            clientId={visit.clientId}
            appointmentId={appointment.id}
            visitStatus={visit.status}
            tagString={visit.tagString || visit.client?.tagString}
            firstVisit={visit.firstVisit}
            firstName={visit.firstName}
            lastName={visit.lastName}
            email={visit.email}
            phoneNumber={visit.phoneNumber}
          />
        </Box>
        <Chip size="small" label={t(statusConfig.labelKey)} color={statusConfig.color} />
      </Box>
      {/* Row 2: Visit Details (all visible, compact) */}
      <Stack spacing={0.25} sx={{ mb: 1 }}>
        {/* Guest visit */}
        {visit.chaperoneUserId && (
          <Typography variant="caption" color="text.secondary">
            {t('guestVisit')}
            {(visit as any).chaperoneVisit?.userName && (
              <> {t('with')} {(visit as any).chaperoneVisit.userName}</>
            )}
          </Typography>
        )}

        {/* Fees charged */}
        {visit.lateFee?.id && (
          <Typography variant="caption" color="error.main">
            {t('lateFee')}: {formatCurrency(visit.lateFee.amount, { currency: session.defaultCurrency })}
          </Typography>
        )}
        {visit.noshowFee?.id && (
          <Typography variant="caption" color="error.main">
            {t('noShowFee')}: {formatCurrency(visit.noshowFee.amount, { currency: session.defaultCurrency })}
          </Typography>
        )}

        {/* Resources */}
        {hasFeatureFlag(session, FeatureFlag.ResourceManagement) && (
          <>
            {(visit as any).noResources ? (
              <Typography variant="caption" color="text.secondary">
                {t('assetsDisabled')}
                {canManage && (
                  <Link component="button" variant="caption" sx={{ ml: 0.5 }}>
                    {t('enable')}
                  </Link>
                )}
              </Typography>
            ) : visit.resources && visit.resources.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t('assets')} {visit.resources.map((r) => r.name).join(', ')}
                {canManage && (
                  <Link component="button" variant="caption" sx={{ ml: 0.5 }}>
                    {t('disable')}
                  </Link>
                )}
              </Typography>
            ) : null}
          </>
        )}

        {/* Workout links */}
        {visit.workoutId ? (
          <Link
            href={externalLinks.clientJournalWorkout(
              visit.userId,
              visit.workoutDate,
              undefined,
              useNewJournal
            )}
            target="_blank"
            variant="caption"
          >
            {t('viewWorkouts')}
          </Link>
        ) : appointment.planWorkoutIds && appointment.planWorkoutIds.length > 0 ? (
          <Link component="button" variant="caption">
            {t('logSessionWorkouts')}
          </Link>
        ) : null}

        {/* Booking info */}
        {visit.createdAt && (
          <Typography variant="caption" color="text.secondary">
            {t('booked')}{visit.bookedByName && <> {t('by')} {visit.bookedByName}</>} {t('on')}{' '}
            {format(new Date(Number(visit.createdAt) * 1000), 'MMM d, yy')}
            {visit.noRequirePrepayment && <> · {t('noPrePayment')}</>}
          </Typography>
        )}

        {/* Cancelled info */}
        {isCancelled && visit.cancelledAt && (
          <Typography variant="caption" color="text.secondary">
            {t('cancelled')}{visit.cancelledByName && <> {t('by')} {visit.cancelledByName}</>} {t('on')}{' '}
            {format(new Date(Number(visit.cancelledAt) * 1000), 'MMM d, yy')}
          </Typography>
        )}
      </Stack>
      {/* Row 3: Package Info */}
      {
        visit.id && (
          <Box sx={{ mb: 1.5 }}>
            <VisitPackageInfo
              visit={visit}
              serviceId={appointment.serviceId}
              onChargePackage={onAssignPackage}
            />
          </Box>
        )
      }
      {/* Row 4: Actions (single-click chips for better tap targets) */}
      {
        canManage && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              pt: 1.5,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {isClosedOut ? (
              /* Closed-out visit actions */
              <>
                <Chip
                  icon={<Icon icon={faRotateRight} size="xsmall" />}
                  label={t('resetStatus')}
                  size="small"
                  variant="outlined"
                  onClick={() => onResetStatus?.(visit)}
                  disabled={isLoading}
                  clickable
                />
                {/* Package deduction toggle - v2 lines 957-970 */}
                {!isCancelled && (
                  <Chip
                    label={
                      visit.paidByPackageId
                        ? t('dontUsePackage')
                        : visit.paid
                          ? t('attemptPackageDeduction')
                          : t('dontUsePackage')
                    }
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (visit.paidByPackageId) {
                        // Has package → remove it
                        onDontUsePackage?.(visit);
                      } else if (visit.paid) {
                        // Paid but no package → try to find one
                        onAttemptPackageDeduction?.(visit);
                      } else {
                        // Not paid → mark as paid without package
                        onDontUsePackage?.(visit);
                      }
                    }}
                    disabled={isLoading}
                    clickable
                  />
                )}
              </>
            ) : (
              /* Active visit actions */
              <>
                <Chip
                  icon={<Icon icon={faCheck} size="xsmall" />}
                  label={t('checkIn')}
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={() => onMarkCompleted?.(visit)}
                  disabled={isLoading}
                  clickable
                />
                <Chip
                  icon={<Icon icon={faUserSlash} size="xsmall" />}
                  label={t('noShowAction')}
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => onMarkNoShow?.(visit, false)}
                  disabled={isLoading}
                  clickable
                />
                {hasNoshowFee && visit.userId && (
                  <Chip
                    icon={<Icon icon={faUserSlash} size="xsmall" />}
                    label={t('noShowPlusFee')}
                    size="small"
                    color="warning"
                    variant="outlined"
                    onClick={() => onMarkNoShow?.(visit, true)}
                    disabled={isLoading}
                    clickable
                  />
                )}
                {canCancelVisit && (
                  <>
                    <Chip
                      icon={<Icon icon={faBan} size="xsmall" />}
                      label={canEarlyCancelVisit ? t('earlyCancel') : t('cancel')}
                      size="small"
                      color={canEarlyCancelVisit ? 'warning' : undefined}
                      variant="outlined"
                      onClick={() => onMarkCancelled?.(visit)}
                      disabled={isLoading}
                      clickable
                    />
                    {hasCancellationFee && (
                      <Chip
                        icon={<Icon icon={faBan} size="xsmall" />}
                        label={t('lateCancelAction')}
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => onMarkLateCancelled?.(visit, false)}
                        disabled={isLoading}
                        clickable
                      />
                    )}
                    {hasCancellationFee && visit.userId && (
                      <Chip
                        icon={<Icon icon={faBan} size="xsmall" />}
                        label={t('latePlusFee')}
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => onMarkLateCancelled?.(visit, true)}
                        disabled={isLoading}
                        clickable
                      />
                    )}
                    {!hasCancellationFee && canEarlyCancelVisit && (
                      <Chip
                        icon={<Icon icon={faBan} size="xsmall" />}
                        label={t('lateCancelAction')}
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => onMarkLateCancelled?.(visit, false)}
                        disabled={isLoading}
                        clickable
                      />
                    )}
                  </>
                )}
                {hasDefaultPackage && hasNoAvailablePackages && (
                  <Chip
                    icon={<Icon icon={faCreditCardFront} size="xsmall" />}
                    label={t('checkInChargeDefault')}
                    size="small"
                    color="success"
                    variant="outlined"
                    onClick={handleChargeDefaultPackage}
                    disabled={isLoading}
                    clickable
                  />
                )}
              </>
            )}
            {/* Common actions */}
            {canCancelVisit && !hasMembership && !isCancelled && (
              <Chip
                icon={<Icon icon={faTrash} size="xsmall" />}
                label={t('cancelAndDelete')}
                size="small"
                color="error"
                variant="outlined"
                onClick={() => onDeleteVisit?.(visit)}
                disabled={isLoading}
                clickable
              />
            )}
            {/* Additional actions - always available when canManage */}
            {visit.userId && (
              <Chip
                icon={<Icon icon={faCreditCardFront} size="xsmall" />}
                label={visit.userCardOnFile ? t('updateCard') : t('addCard')}
                size="small"
                variant="outlined"
                onClick={() => onAddCard?.(visit)}
                disabled={isLoading}
                clickable
              />
            )}
            <Chip
              icon={<Icon icon={faFilePen} size="xsmall" />}
              label={t('notes')}
              size="small"
              variant="outlined"
              onClick={() => onManageNotes?.(visit)}
              disabled={isLoading}
              clickable
            />
            {isRecurring && visit.userId && (
              <Chip
                icon={<Icon icon={faCalendarDays} size="xsmall" />}
                label={hasMembership ? t('editRecurring') : t('createRecurring')}
                size="small"
                variant="outlined"
                onClick={() => onManageRecurringMembership?.(visit)}
                disabled={isLoading}
                clickable
              />
            )}
            {visit.userId && (
              <Chip
                icon={<Icon icon={faCreditCard} size="xsmall" />}
                label={t('chargeAccount')}
                size="small"
                variant="outlined"
                onClick={() => onChargeProduct?.(visit)}
                disabled={isLoading}
                clickable
              />
            )}
          </Box>
        )
      }
      <ConfirmDialog {...confirmDialogProps} />
    </Box >
  );
};
